/**
 * WorkflowPermissionsEditor — thin adapter around the shared PermissionEditor.
 *
 * sl-za0v.10.3 + sl-2ykq.3: Wires REST API endpoints for workflow role
 * management into the modular permission editor.
 *
 * API: /api/workspace/:workspaceId/workflow/:workflowId/roles (GET, POST, DELETE)
 *
 * Workflow roles follow the unified RBAC pattern (Owner > Admin > Editor > Reader):
 * - Owner: Full access + ownership transfer
 * - Admin: Full access (edit, run, delete, publish, manage permissions)
 * - Editor: Can edit + run + view results
 * - Reader: Can view results only
 */
import {
  defaultWorkspaceApiClient,
  WorkspaceApiError,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getGroupsQuery, getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useMemo } from 'react';

import { PermissionEditor } from '../shared/permission-editor';
import type {
  PermissionEditorAdapter,
  PermissionEditorConfig,
  PermissionEntry,
} from '../shared/permission-editor-types';

// =============================================================================
// Config — Unified RBAC: Owner > Admin > Editor > Reader
// =============================================================================

const WORKFLOW_PERMISSION_CONFIG: PermissionEditorConfig = {
  allRoles: ['Owner', 'Admin', 'Editor', 'Reader'],
  grantableRoles: ['Admin', 'Editor', 'Reader'],
  ownerRole: 'Owner',
  demoteOnTransferRole: 'Admin',
  defaultGrantRole: 'Editor',
  testIdPrefix: 'workflow-permissions',
};

function workflowRoleRequestKey(
  workflowId: string,
  action: string,
  target?: string
) {
  return `workflow-roles:${workflowId}:${action}${target ? `:${target}` : ''}`;
}

// =============================================================================
// REST API Response Type
// =============================================================================

interface WorkflowRoleResponse {
  id: string;
  type: 'user' | 'group';
  targetId: string;
  targetName: string;
  targetEmail?: string;
  targetAvatarUrl?: string | null;
  role: string;
}

// =============================================================================
// Component
// =============================================================================

interface WorkflowPermissionsEditorProps {
  workflowId: string;
}

export const WorkflowPermissionsEditor = ({
  workflowId,
}: WorkflowPermissionsEditorProps) => {
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;

  const adapter = useMemo(
    (): PermissionEditorAdapter => ({
      async loadEntries(): Promise<PermissionEntry[]> {
        const json = await defaultWorkspaceApiClient
          .requestJson<{ roles?: WorkflowRoleResponse[] }>({
            workspaceId,
            path: `/api/workspace/${workspaceId}/workflow/${workflowId}/roles`,
            category: 'settings',
            key: workflowRoleRequestKey(workflowId, 'list'),
            mode: 'background',
            priority: 'visible',
          })
          .catch(error => {
            if (error instanceof WorkspaceApiError && error.status === 403) {
              return { roles: [] };
            }
            throw error;
          });
        const roles: WorkflowRoleResponse[] = json.roles ?? [];

        return roles.map(r => {
          if (r.type === 'user') {
            return {
              type: 'user' as const,
              id: r.id,
              targetId: r.targetId,
              name: r.targetName ?? '',
              email: r.targetEmail ?? '',
              avatarUrl: r.targetAvatarUrl ?? null,
              role: r.role,
            };
          }
          return {
            type: 'group' as const,
            id: r.id,
            targetId: r.targetId,
            name: r.targetName,
            role: r.role,
          };
        });
      },

      async loadMembers(query: string) {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            take: 50,
            query: query || undefined,
          },
        });
        return (result.workspace?.members ?? []).map(m => ({
          id: m.id,
          name: m.name ?? '',
          email: m.email ?? '',
          avatarUrl: m.avatarUrl ?? null,
        }));
      },

      async loadGroups() {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getGroupsQuery,
          variables: { workspaceId },
        });
        return (result.groups ?? []).map(
          (g: { id: string; name: string; description?: string | null }) => ({
            id: g.id,
            name: g.name,
            description: g.description ?? undefined,
          })
        );
      },

      async grantUser(userId: string, role: string) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/workflow/${workflowId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'user', targetId: userId, role }),
          },
          category: 'settings',
          key: workflowRoleRequestKey(workflowId, 'grant-user', userId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async grantGroup(groupId: string, role: string) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/workflow/${workflowId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'group', targetId: groupId, role }),
          },
          category: 'settings',
          key: workflowRoleRequestKey(workflowId, 'grant-group', groupId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async updateRole(entry: PermissionEntry, newRole: string) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/workflow/${workflowId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: entry.type,
              targetId: entry.targetId,
              role: newRole,
            }),
          },
          category: 'settings',
          key: workflowRoleRequestKey(workflowId, 'update', entry.targetId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async revoke(entry: PermissionEntry) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/workflow/${workflowId}/roles/${entry.id}`,
          init: {
            method: 'DELETE',
          },
          category: 'settings',
          key: workflowRoleRequestKey(workflowId, 'revoke', entry.id),
          mode: 'foreground',
          priority: 'foreground',
        });
      },
    }),
    [serverService.server, workspaceId, workflowId]
  );

  return (
    <PermissionEditor config={WORKFLOW_PERMISSION_CONFIG} adapter={adapter} />
  );
};
